import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Click-to-open Terms & Conditions popup. Rendered as a small inline
 * trigger ("*Terms and conditions apply.") that, when tapped, surfaces
 * the full operating rules: cancellations, court etiquette, and the
 * acknowledgement-of-risk waiver.
 *
 * Used in the Payment page's Booking Summary card.
 */
const TermsConditionsDialog = ({
  triggerLabel = "*Terms and conditions apply.",
  triggerClassName = "text-[11px] italic text-[#a1a1aa] underline underline-offset-2 hover:text-[#8cc63f] transition-colors cursor-pointer",
}) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        data-testid="terms-conditions-trigger"
        onClick={() => setOpen(true)}
        className={triggerClassName}
      >
        {triggerLabel}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="terms-conditions-dialog"
          className="bg-[#121212] text-[#e4e4e7] max-w-xl max-h-[90vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle className="text-2xl font-black tracking-tighter">
              Terms &amp; Conditions
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 text-sm leading-relaxed">
            {/* Cancellations and Refunds */}
            <section
              data-testid="tc-cancellations-section"
              className="rounded-sm border border-white/10 bg-white/[0.02] p-4"
            >
              <h3 className="font-bold text-[#8cc63f] mb-3 uppercase tracking-wide text-xs">
                Cancellations and Refunds
              </h3>
              <ul className="space-y-2 list-disc list-inside marker:text-[#8cc63f]">
                <li>
                  <span className="font-semibold text-white">Notice Period:</span>{" "}
                  Cancellations made 24+ hours in advance are non-refundable, but can
                  be rebooked for a later date.
                </li>
                <li>
                  <span className="font-semibold text-white">Late Cancellations:</span>{" "}
                  Cancellations received less than 24 hours before the scheduled time
                  are not eligible for a refund.
                </li>
                <li>
                  <span className="font-semibold text-white">Weather:</span> If, in the
                  sole discretion of management, weather conditions make it unsafe or
                  unreasonable to operate, we reserve the right to cancel. In such
                  cases, customers will be offered a full refund or the option to
                  reschedule.
                </li>
              </ul>
            </section>

            {/* Court Rules & Etiquette */}
            <section
              data-testid="tc-court-rules-section"
              className="rounded-sm border border-white/10 bg-white/[0.02] p-4"
            >
              <h3 className="font-bold text-[#8cc63f] mb-3 uppercase tracking-wide text-xs">
                Court Rules &amp; Etiquette
              </h3>
              <ul className="space-y-2 list-disc list-inside marker:text-[#8cc63f]">
                <li>
                  <span className="font-semibold text-white">Proper Sports Attire</span>{" "}
                  — Wear athletic clothing designed for comfort and movement. Avoid
                  jeans, denim, slippers, or footwear that may damage the court or
                  compromise safety.
                </li>
                <li>
                  <span className="font-semibold text-white">Food &amp; Drinks</span>{" "}
                  — Outside food and drinks are allowed in the benches and designated
                  dining areas only.
                </li>
                <li>
                  <span className="font-semibold text-white">Court Conduct</span> —
                  Respect court schedules and player rotations. Play fair, stay kind,
                  and avoid rough play, profanity, or disruptive behavior to ensure an
                  enjoyable experience for everyone.
                </li>
                <li>
                  <span className="font-semibold text-white">Cleanliness &amp; Care</span>{" "}
                  — Mind your belongings and take only what&rsquo;s yours. Keep the
                  area clean, and segregate plastic bottles and cups in the designated
                  recycling box.
                </li>
                <li>
                  <span className="font-semibold text-white">Pets &amp; Kids</span> —
                  We love a family-friendly space! Pets must be on leash and
                  supervised at all times. Kids are welcome but should stay off the
                  courts unless playing or accompanied by an adult.
                </li>
              </ul>
            </section>

            {/* Acknowledgment of Risk and Waiver of Liability */}
            <section
              data-testid="tc-waiver-section"
              className="rounded-sm border border-[#8cc63f]/30 bg-[#8cc63f]/5 p-4"
            >
              <h3 className="font-bold text-[#8cc63f] mb-3 uppercase tracking-wide text-xs">
                Acknowledgment of Risk &amp; Waiver of Liability
              </h3>
              <p className="text-[#e4e4e7]">
                By booking or entering Assenda, I acknowledge that pickleball involves
                inherent risks (including injury, death, or property damage) and
                voluntarily assume all such risks. To the fullest extent permitted by
                law, I release and hold harmless Assenda, its owners, staff, and
                affiliates from any liability, including negligence, agree to follow
                all rules and confirm I am fit to participate, and accept
                responsibility for my belongings and any claims arising from my
                actions.
              </p>
            </section>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TermsConditionsDialog;
